import { useCallback, useEffect, useRef, useState } from "react";
import {
    applyAutocompleteSelection,
    applyPatientFieldUpdate,
    createEmptyMedicament,
    createEmptyPatient,
    invalidatePatientAfterProductAddition,
    withPatientAt,
} from "./patientFormRules";

function useSelectedIndex(patientCount) {
    const [selectedIdx, setSelectedIdx] = useState(0);
    useEffect(() => {
        if (selectedIdx >= patientCount && patientCount > 0) {
            setSelectedIdx(patientCount - 1);
        }
    }, [patientCount, selectedIdx]);
    return [selectedIdx, setSelectedIdx];
}

function useFocusRequest() {
    const [requested, setRequested] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (requested && ref.current) {
            ref.current.focus();
            setRequested(false);
        }
    }, [requested]);
    const request = useCallback(() => setRequested(true), []);
    return [ref, request];
}

export default function usePatientFormEditing({ patients, onChange, onSave, isDirty, medicamentStrategy }) {
    const [selectedIdx, setSelectedIdx] = useSelectedIndex(patients.length);
    const [nomInputRef, requestNomFocus] = useFocusRequest();
    const [focusDrugNomOnAdd, setFocusDrugNomOnAdd] = useState(false);
    const defaultMedicamentDestination = medicamentStrategy?.defaultDestination || "";

    const addPatient = useCallback(() => {
        // Capture le draft courant avant d'ajouter la ligne vide. Cela permet
        // de persister le patient qui vient d'être saisi sans que le nouveau
        // patient incomplet ne bloque la requête.
        if (patients.length > 0 && isDirty && onSave) {
            void onSave();
        }
        const newPatients = [...patients, createEmptyPatient()];
        onChange(newPatients);
        setSelectedIdx(newPatients.length - 1);
        requestNomFocus();
    }, [patients, onChange, onSave, isDirty, setSelectedIdx, requestNomFocus]);

    const updateSelected = useCallback((update) => {
        onChange(withPatientAt(patients, selectedIdx, patient =>
            invalidatePatientAfterProductAddition(patient, update(patient))
        ));
    }, [patients, selectedIdx, onChange]);

    const updatePatientField = useCallback((field, value) => {
        updateSelected(patient => applyPatientFieldUpdate(patient, field, value, defaultMedicamentDestination));
    }, [updateSelected, defaultMedicamentDestination]);

    const selectSuggestion = useCallback((suggestion) => {
        updateSelected(patient => applyAutocompleteSelection(patient, suggestion));
    }, [updateSelected]);

    const setSelectedProduits = useCallback((produits) => {
        updateSelected(patient => ({ ...patient, produits }));
    }, [updateSelected]);

    const removeSelectedPatient = useCallback(() => {
        const newPatients = patients.filter((_, i) => i !== selectedIdx);
        onChange(newPatients);
        setSelectedIdx(prev => Math.min(prev, newPatients.length - 1));
    }, [patients, selectedIdx, onChange, setSelectedIdx]);

    const addDrugToSelected = useCallback(() => {
        if (patients.length === 0) return;
        updateSelected(patient => ({
            ...patient,
            produits: [...(patient.produits || []), createEmptyMedicament(defaultMedicamentDestination)],
        }));
        setFocusDrugNomOnAdd(true);
    }, [patients.length, updateSelected, defaultMedicamentDestination]);

    const clearDrugNomFocus = useCallback(() => setFocusDrugNomOnAdd(false), []);

    return {
        selectedIdx,
        setSelectedIdx,
        nomInputRef,
        focusDrugNomOnAdd,
        clearDrugNomFocus,
        addPatient,
        updateSelected,
        updatePatientField,
        selectSuggestion,
        setSelectedProduits,
        removeSelectedPatient,
        addDrugToSelected,
    };
}
